import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Globe } from 'lucide-react';
import { WEBSITE_CREATION_TIERS, WC_CURRENCY_SYMBOL } from '@/lib/websiteCreationPricing';
import { detectDefaultCurrency, setStoredCurrency } from '@/lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'lawagency360@gmail.com';

function formatAmount(n) { return Number(n).toLocaleString('en-US'); }

/**
 * Paywall modal opened when a client without an active Website Creation plan
 * clicks "Submit Brief" / "Submit Query".  Persists the brief draft, then
 * forwards the user to the correct Stripe Payment Link for the chosen tier
 * + currency.
 */
export default function WebsitePaywallModal({ open, onClose, onSavePendingBrief }) {
  const [links, setLinks] = useState(null);
  const [currency, setCurrencyState] = useState(() => detectDefaultCurrency());
  const setCurrency = (c) => { setCurrencyState(c); setStoredCurrency(c); };
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    fetch(`${API}/stripe/website-creation-links`).then(r => r.json()).then(setLinks).catch(() => setLinks({}));
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChoose = async (tierId) => {
    setLoading(tierId);
    try {
      if (onSavePendingBrief) onSavePendingBrief(tierId);
      // Mark "paying now" so the dashboard polls billing on return
      try { localStorage.setItem('website_creation_paying_at', new Date().toISOString()); } catch (_e) { /* ignore */ }
      const url = links?.[tierId]?.[currency];
      const isPlaceholder = !url || url.toLowerCase().includes('paste payment link');
      if (!isPlaceholder) {
        window.location.href = url;
        return;
      }
      const tier = WEBSITE_CREATION_TIERS.find(t => t.id === tierId);
      const subject = encodeURIComponent(`Activate Website Creation - ${tier?.label || tierId}`);
      const body = encodeURIComponent(
        `Hi Law Agency 360 team,\n\nPlease activate the ${tier?.label || tierId} plan so I can submit my project brief.\n\nThanks!`
      );
      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    } finally {
      setLoading(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="website-paywall-title"
      data-testid="website-paywall-modal"
    >
      <button
        type="button"
        aria-label="Close paywall"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-violet-500/30 p-7 shadow-2xl shadow-violet-900/40 my-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="website-paywall-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
            <Globe className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h2 id="website-paywall-title" className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              Choose a Website Creation Plan
            </h2>
            <p className="text-sm text-zinc-400 mt-0.5">
              Select a plan to proceed with your website creation
            </p>
          </div>
        </div>

        {/* Currency toggle */}
        <div className="flex justify-end mt-5 mb-4">
          <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-full" data-testid="website-paywall-currency">
            {[
              { code: 'gbp', label: 'GBP £' },
              { code: 'eur', label: 'EUR €' },
              { code: 'usd', label: 'USD $' },
            ].map(c => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                  currency === c.code ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`website-paywall-currency-${c.code}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4" data-testid="website-paywall-tiers">
          {WEBSITE_CREATION_TIERS.map(t => {
            const sym = WC_CURRENCY_SYMBOL[currency] || '£';
            const amt = t.price[currency];
            return (
              <div
                key={t.id}
                className={`relative flex flex-col rounded-2xl p-6 border transition-all ${
                  t.featured
                    ? 'bg-gradient-to-b from-violet-900/30 to-indigo-900/20 border-violet-500/40 shadow-xl shadow-violet-900/30'
                    : 'bg-slate-800/40 border-white/10 hover:border-white/20'
                }`}
                data-testid={`website-paywall-tier-${t.id}`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
                    Recommended
                  </div>
                )}
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{t.label}</h3>
                <p className="text-sm text-zinc-400 mb-5">{t.description}</p>
                <div className="flex items-center gap-2 mb-5">
                  <span className="text-3xl font-bold text-white" data-testid={`website-paywall-price-${t.id}`}>
                    {sym}{formatAmount(amt)}
                  </span>
                  <span className="text-xs text-zinc-500">one-off</span>
                </div>
                <div className="flex items-center gap-2 mb-6 text-sm text-zinc-200">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span data-testid={`website-paywall-pages-${t.id}`}>{t.pages}</span>
                </div>
                <button
                  onClick={() => handleChoose(t.id)}
                  disabled={loading === t.id}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                    t.featured
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-violet-600/40'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                  data-testid={`website-paywall-choose-${t.id}`}
                >
                  {loading === t.id ? 'Redirecting…' : 'Choose Plan'}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-zinc-500 mt-5 text-center">
          Your brief is saved as a draft until payment completes - we'll auto-submit once your plan activates.
        </p>
      </div>
    </div>,
    document.body
  );
}
